import numeral from 'numeral';
import isEmpty from 'lodash/isEmpty';
import { parseNumber, formatNumber } from 'libphonenumber-js';

const numberUtils = {
    compact(rawNum) {
        let abbrev = '';
        let number;
        // WARNING/TODO: DOES NOT WORK if rawNum includes COMMAS! see tests.
        /**
         * Decimal adjustment of a number.
         * Handy function to properly round decimal numbers.
         * Using this, we get 3.195 => 3.2
         * JavaScript's toFixed(2) method would return 3.19
         *
         * @param {String}  type  The type of adjustment.
         * @param {Number}  value The number.
         * @param {Integer} exp   The exponent (the 10 logarithm of the adjustment base).
         * @returns {Number} The adjusted value.
         */
        function roundDecimal(type, value, exp) {
            // If the exp is undefined or zero...
            if (typeof exp === 'undefined' || Number(exp) === 0) {
                return Math[type](value);
            }
            value = Number(value);
            exp = Number(exp);
            // If the value is not a number or the exp is not an integer...
            if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) {
                return NaN;
            }
            // Shift
            value = value.toString().split('e');
            value = Math[type](Number(`${value[0]}e${value[1] ? (Number(value[1]) - exp) : -exp}`));
            // Shift back
            value = value.toString().split('e');
            return Number(`${value[0]}e${value[1] ? (Number(value[1]) + exp) : exp}`);
        }

        if (Array.isArray(rawNum)) {
            rawNum = rawNum[0];
        }

        // Make sure we convert input to number and remove any commas and such.
        rawNum = Number(rawNum.toString().split(',').join(''));

        if (rawNum >= 1000000) {
            // Result: 2,350,000 / 10,000 => 2.35m
            number = rawNum / 1000000;
            abbrev = 'm';
            number = roundDecimal('round', number, -2);
        } else if (rawNum >= 100000) {
            // Result: 235,500 / 1,000 => 235k
            number = Math.floor((rawNum / 1000)).toFixed(0);
            abbrev = 'k';
        } else if (rawNum >= 10000) {
            // Result: 10,500 / 1,000 => 10.5k
            number = (rawNum / 1000).toFixed(1);
            if (number[number.length - 1] === '0') {
                number = number.slice(0, -2);
            }
            abbrev = 'k';
        } else if (rawNum >= 0) {
            // 1234 => 1,234
            number = numberUtils.prettyPrintNumber(rawNum);
        } else {
            number = '--';
        }

        return number + abbrev;
    },
    fullNumber(rawNum) {
        if (rawNum && rawNum >= 0) {
            return numberUtils.prettyPrintNumber(rawNum);
        }

        return '--';
    },
    asPercent(value) {
        const percent = numeral(value);

        return numeral(percent)
            .divide(100)
            .format('0%');
    },
    asCurrency(value, format = '$0,0') {
        const amount = numeral(value);

        return numeral(amount).format(format);
    },
    asCurrencyWithDefault(value, defaultValue) {
        return value ? this.asCurrency(value, '$0,0.00') : defaultValue;
    },
    removeNonNumeric(value) {
        return value.replace(/[^0-9]/g, '');
    },
    prettyPrintNumber(int) {
        const parts = int.toString().split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return parts.join('.');
    },
    cleanPhoneNumber(phone) {
        // Strip any user formatting from phone numbers because we
        // handle how to display this on the front-end.
        // e.g., "(123) 456-7890" -> "1234567890"
        phone = phone.replace(/[^\d]/g, '');

        if (phone.length > 10) {
            phone = phone.substring(0, 10);
        }

        return phone;
    },
    formatPhoneNumberInput(phone) {
        if (typeof phone === 'undefined') {
            return '';
        }

        let phoneNumber = numberUtils.cleanPhoneNumber(phone);
        const phoneLength = phoneNumber.length;

        // Check the length of the phone number and add appropriate masking based on length.
        if (phoneLength === 10) {
            phoneNumber =
                `(${
                    phoneNumber.substring(0, 3)
                }) ${
                    phoneNumber.substring(3, 6)
                }-${
                    phoneNumber.substring(6, 10)}`;
        } else if (phoneLength > 6) {
            phoneNumber =
                `(${
                    phoneNumber.substring(0, 3)
                }) ${
                    phoneNumber.substring(3, 6)
                }-${
                    phoneNumber.substring(6, phoneLength)}`;
        } else if (phoneLength > 3) {
            phoneNumber = `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(3, phoneLength)}`;
        } else if (phoneLength > 0) {
            phoneNumber = `(${phoneNumber.substring(0, phoneLength)}`;
        }

        return phoneNumber;
    },
    formatPhoneNumber(phone) {
        if (typeof phone === 'undefined') {
            return '';
        }
        const parsedPhone = parseNumber(phone, 'US', { extended: true });
        if (isEmpty(parsedPhone) || (!parsedPhone.valid && !parsedPhone.possible)) {
            return phone;
        }
        const formatType = parsedPhone.country === 'US' ? 'National' : 'International';
        return formatNumber(parsedPhone, formatType);
    },
    /**
     * Returns formatted number with suffix 'k'
     *
     * @param {Number} value
     *
     * ex: 1000 returns 1k
     */
    formatWithK(value) {
        return (Math.abs(value) > 999
            ? `${Math.sign(value) * ((Math.abs(value) / 1000).toFixed(1))}k`
            : Math.sign(value) * Math.abs(value)
        );
    },
    isOnlyIntegers(value) {
        return !!(`${value}`).match(/^[0-9]+$/);
    },
};

export default numberUtils;
